/**
 * SecureEmbed Period Selector
 *
 * Reusable period/time-range selector component used across Search and Reports pages.
 * Supports preset ranges, custom date picker, and disabled/greyed-out options.
 */

import React from 'react';
import { Calendar } from 'lucide-react';

export interface PeriodRange {
  key: string;
  label: string;
  days: number;
}

export const DEFAULT_PERIOD_RANGES: PeriodRange[] = [
  { key: '7d', label: '7D', days: 7 },
  { key: '30d', label: '30D', days: 30 },
  { key: '90d', label: '90D', days: 90 },
  { key: '1y', label: '1Y', days: 365 },
  { key: '5y', label: '5Y', days: 1825 },
  { key: 'all', label: 'ALL', days: 0 },
];

interface PeriodSelectorProps {
  /** Currently selected time range key */
  value: string;
  /** Callback when range changes */
  onChange: (key: string) => void;
  /** Custom start date (ISO string or date input value) */
  customStart?: string;
  /** Custom end date */
  customEnd?: string;
  /** Callback for custom start date */
  onCustomStartChange?: (value: string) => void;
  /** Callback for custom end date */
  onCustomEndChange?: (value: string) => void;
  /** Optional list of range keys to disable/grey out */
  disabledRanges?: string[];
  /** Optional override for available ranges */
  ranges?: PeriodRange[];
  /** Optional label (default: 'Period:') */
  label?: string;
  /** Optional hint text on the right */
  hint?: string;
}

const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  value,
  onChange,
  customStart = '',
  customEnd = '',
  onCustomStartChange,
  onCustomEndChange,
  disabledRanges = [],
  ranges = DEFAULT_PERIOD_RANGES,
  label = 'Period:',
  hint = 'Applied to search & reports',
}) => {
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4" />
          <span className="font-medium">{label}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {ranges.map(r => {
            const isDisabled = disabledRanges.includes(r.key);
            return (
              <button
                key={r.key}
                onClick={() => !isDisabled && onChange(r.key)}
                disabled={isDisabled}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  value === r.key
                    ? 'bg-[#2B064F] text-white'
                    : isDisabled
                      ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {r.label}
              </button>
            );
          })}
          <button
            onClick={() => onChange('custom')}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              value === 'custom'
                ? 'bg-[#2B064F] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Custom Range
          </button>
        </div>
        {value === 'custom' && onCustomStartChange && onCustomEndChange && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customStart}
              onChange={(e) => onCustomStartChange(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2B064F]"
            />
            <span className="text-xs text-gray-400">to</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => onCustomEndChange(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2B064F]"
            />
          </div>
        )}
        {hint && (
          <span className="text-xs text-gray-400 ml-auto">{hint}</span>
        )}
      </div>
    </div>
  );
};

export default PeriodSelector;
